// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IconAddProject from "@conklin/XDL/Icons/IconAddProject";
import IconCheckmark from "@conklin/XDL/Icons/IconCheckmark";
import IconMail from "@conklin/XDL/Icons/IconMail";
import IconPrint from "@conklin/XDL/Icons/IconPrint";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, createLinkIconStyles, xScreen } from "@conklin/XDL/Utilities";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Save from "@insite/mobius/Icons/Save";
import Trash2 from "@insite/mobius/Icons/Trash2";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { css } from "styled-components";

interface MyAccountDetailsStyles {
    myAccountDetailsActions?: {
        saveOrderLink?: LinkPresentationProps;
        placeOrderLink?: LinkPresentationProps;
        acceptLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAllLink?: LinkPresentationProps;
        printLink?: LinkPresentationProps;
        emailLink?: ShareEntityButtonStyles;
    };
}
const iconStyles: IconPresentationProps = {
    size: 12,
};
const typographyStyles: TypographyPresentationProps = {
    size: 12,
    lineHeight: "15px",
    weight: fontConfig.weight.bold,
    css: css`
        &&&& {
            color: ${color("common.ui9")};
        }
    `,
};

const myAccountDetailsStyles: MyAccountDetailsStyles = {
    myAccountDetailsActions: {
        saveOrderLink: {
            ...createLinkIconStyles(Save, "small", "left", false, {}, typographyStyles, iconStyles),
        },
        placeOrderLink: {
            ...createLinkIconStyles(Save, "small", "left", false, {}, typographyStyles, iconStyles),
        },
        acceptLink: {
            ...createLinkIconStyles(IconCheckmark, "small", "left", false, {}, typographyStyles, iconStyles),
        },
        addAllToListClickable: {
            ...createLinkIconStyles(IconAddProject, "small", "left", false, {}, typographyStyles, iconStyles),
        },
        removeAllLink: {
            ...createLinkIconStyles(Trash2, "small", "left", false, {}, typographyStyles, iconStyles),
        },
        printLink: {
            ...createLinkIconStyles(IconPrint, "small", "left", false, {}, typographyStyles, {
                size: 12,
                css: css`
                    &,
                    & > svg {
                        height: 13px;
                    }
                `,
            }),
        },
        emailLink: {
            button: {
                variant: "secondary",
                icon: {
                    src: IconMail,
                    position: "left",
                },
                css: css`
                    min-height: 15px;
                    border-color: transparent;
                    padding: 0;
                    ${IconWrapper} {
                        opacity: 0.5;
                    }
                    &:hover {
                        border-color: transparent;
                    }
                `,
            },
        },
    },
};

export const linkLikeButtonCss: any = {
    icon: {
        position: "left",
    },
    typographyProps: {
        size: 12,
        lineHeight: "15px",
        weight: "bold",
        color: "common.ui9",
    },
    css: css`
        padding: 0;
        margin: 0;
        transition: none;
        border: none;
        min-height: auto;
        ${IconWrapper}, ${IconWrapper} svg {
            width: 12px;
            height: 13px;
            color: ${color("common.ui9")};
        }
        &&&:hover,
        &&&:focus {
            border: none;
            outline: none;
        }
    `,
};

export const createListDetailButtonStyle = (iconSrc?: React.ComponentType | string) => {
    return {
        variant: "secondary",
        icon: {
            src: iconSrc,
            position: "right",
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border-color: transparent;

            ${IconWrapper} {
                opacity: 0.5;
                margin-right: 6px;
            }
        `,
    } as ButtonPresentationProps;
};

export const cartLineWithNotes: any = {
    remove: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-column: 1 / 7;
                grid-row: 6 / 7;
            }
        `,
    },
    notes: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-column: 1 / 7;
                grid-row: 3 / 4;
                padding-top: 4px;
                padding-bottom: 12px;
            }
        `,
    },
    inventory: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-column: 1 / 7;
                grid-row: 4 / 5;
            }
        `,
    },
    qty: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-row: 5 / 6;
            }
        `,
    },
    total: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-row: 5 / 6;
                grid-column: 4 / 7;
            }
        `,
    },
    description: {
        css: css`
            ${xScreen("lte", "md")} {
                grid-column: 2 / 7;
            }
        `,
    },
};

export default myAccountDetailsStyles;
